import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Info, LoaderCircle } from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import {
  getDashboardActivationRenewalStartAt,
  type DashboardUserProfile,
} from '@services/firebase'
import { submitPaypalPremiumOrder } from '@services/firebase/premium'
import { waitForActivatedDashboardProfile } from '@features/premium/shared/premium-profile'
import { loadPaypalSdk } from '@features/premium/shared/paypal-sdk'
import { getPaypalClientId } from '@features/premium/shared/premium-runtime'
import type { PremiumPlan } from '@features/premium/shared/premium-plans'
import { PremiumLegacySurface } from './PremiumLegacySurface'

interface PayPalCheckoutCardProps {
  readonly onActivated: (profile: DashboardUserProfile) => void
  readonly plan: PremiumPlan
  readonly profile: DashboardUserProfile
}

function parsePaypalCreateTime(value?: string) {
  if (!value) {
    return Date.now()
  }

  const timestamp = Date.parse(value)

  return Number.isNaN(timestamp) ? Date.now() : timestamp
}

export function PayPalCheckoutCard({
  onActivated,
  plan,
  profile,
}: PayPalCheckoutCardProps) {
  const paypalContainerRef = useRef<HTMLDivElement | null>(null)
  const { enqueueSnackbar } = useSnackbar()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const paypalClientId = getPaypalClientId()
  const [isLoadingSdk, setIsLoadingSdk] = useState(Boolean(paypalClientId))
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!paypalClientId) {
      return
    }

    let isMounted = true
    const containerElement = paypalContainerRef.current
    let closeButtons:
      | (() => Promise<void> | void)
      | undefined

    async function mountPaypalButtons() {
      try {
        setIsLoadingSdk(true)
        setErrorMessage(null)
        const paypal = await loadPaypalSdk(paypalClientId)

        if (!isMounted || !containerElement) {
          return
        }

        containerElement.innerHTML = ''

        const buttons = paypal.Buttons({
          async createOrder(_data, actions) {
            return actions.order.create({
              application_context: {
                shipping_preference: 'NO_SHIPPING',
              },
              purchase_units: [
                {
                  amount: {
                    value: plan.price.toFixed(2),
                  },
                },
              ],
            })
          },
          async onApprove(data, actions) {
            try {
              setIsSubmitting(true)
              setErrorMessage(null)
              const details = await actions.order.capture()
              const fallbackStartAt = parsePaypalCreateTime(details.create_time)
              const startAt = getDashboardActivationRenewalStartAt(
                profile.activation_key,
                fallbackStartAt,
              )
              const activationCode = await submitPaypalPremiumOrder({
                duration: plan.durationMonths,
                olderKeyId: profile.activation_key?.id ?? null,
                orderId: data.orderID,
                price: plan.price,
                startAt,
              })

              const latestProfile = await waitForActivatedDashboardProfile({
                activationCode,
              })

              onActivated(latestProfile)
              enqueueSnackbar('Payment success', { variant: 'success' })
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Payment approval failed.'

              setErrorMessage(message)
              enqueueSnackbar(message, { variant: 'error' })
            } finally {
              setIsSubmitting(false)
            }
          },
          onError(error) {
            const message =
              error instanceof Error
                ? error.message
                : 'PayPal checkout failed to load.'

            setErrorMessage(message)
            enqueueSnackbar(message, { variant: 'error' })
          },
          style: {
            color: 'gold',
            label: 'paypal',
            layout: 'vertical',
            shape: 'rect',
          },
        })

        closeButtons = buttons.close
        await buttons.render(containerElement)

        if (isMounted) {
          setIsLoadingSdk(false)
        }
      } catch (error) {
        if (!isMounted) {
          return
        }

        const message =
          error instanceof Error
            ? error.message
            : 'PayPal SDK failed to load.'

        setErrorMessage(message)
        setIsLoadingSdk(false)
      }
    }

    void mountPaypalButtons()

    return () => {
      isMounted = false
      void closeButtons?.()

      if (containerElement) {
        containerElement.innerHTML = ''
      }
    }
  }, [
    enqueueSnackbar,
    onActivated,
    paypalClientId,
    plan.durationMonths,
    plan.price,
    profile.activation_key?.endAt,
    profile.activation_key?.id,
  ])

  if (!paypalClientId) {
    return (
      <PremiumLegacySurface>
        <div className="premiumStack premiumStack--gap-2">
          <h3 className="premiumSectionTitle premiumTypography premiumTypography--h5">
            Pay with PayPal
          </h3>
          <div className="premiumCheckoutNotice" role="note">
            <Info aria-hidden="true" size={18} strokeWidth={2.5} />
            <span className="premiumTypography premiumTypography--body2">
              PayPal checkout is unavailable in this environment right now.
            </span>
          </div>
        </div>
      </PremiumLegacySurface>
    )
  }

  return (
    <PremiumLegacySurface>
      <div className="premiumStack premiumStack--gap-2_5">
        <h3 className="premiumSectionTitle premiumTypography premiumTypography--h5">
          Pay with PayPal
        </h3>

        <p className="premiumCheckoutPlanSummary premiumTypography premiumTypography--body1">
          Selected plan: ${plan.price} for {plan.durationMonths} months
        </p>

        <div className="premiumPayPalButtonHost" ref={paypalContainerRef} />

        {isLoadingSdk || isSubmitting ? (
          <div
            aria-live="polite"
            className="premiumCheckoutStatus"
            role="status"
          >
            <LoaderCircle
              aria-hidden="true"
              className="premiumCheckoutSpinner"
              size={18}
              strokeWidth={2.5}
            />
            <span className="premiumTypography premiumTypography--body2">
              {isSubmitting ? 'Finalizing checkout' : 'Loading PayPal checkout'}
            </span>
          </div>
        ) : null}

        {errorMessage ? (
          <div
            aria-live="assertive"
            className="premiumCheckoutNotice isError"
            role="alert"
          >
            <AlertCircle aria-hidden="true" size={18} strokeWidth={2.5} />
            <span className="premiumTypography premiumTypography--body2">
              {errorMessage}
            </span>
          </div>
        ) : null}
      </div>
    </PremiumLegacySurface>
  )
}
